import React, { useState, useEffect, useCallback } from 'react';
import { activityAPI } from '../services/api';
import { FiRefreshCw, FiAlertTriangle, FiActivity, FiFilter } from 'react-icons/fi';
import './Students.css';
import './ClientVertical.css';

const ACTIVITY_TYPE_LABELS = {
  STUDENT_CREATED: 'Student Created',
  STUDENT_UPDATED: 'Student Updated',
  STUDENT_DELETED: 'Student Deleted',
  STUDENT_ASSIGNED: 'Student Assigned',
  STATUS_CHANGED: 'Status Changed',
  INTERACTION_LOGGED: 'Interaction Logged',
  USER_CREATED: 'User Created',
  USER_UPDATED: 'User Updated',
  USER_DELETED: 'User Deleted',
  LOGIN: 'Login',
  LOGOUT: 'Logout',
};

const ACTIVITY_TYPE_COLORS = {
  STUDENT_CREATED: '#43e97b',
  STUDENT_UPDATED: '#4facfe',
  STUDENT_DELETED: '#f5576c',
  STUDENT_ASSIGNED: '#011ff3',
  STATUS_CHANGED: '#f5a623',
  INTERACTION_LOGGED: '#38f9d7',
  USER_CREATED: '#43e97b',
  USER_UPDATED: '#4facfe',
  USER_DELETED: '#f5576c',
  LOGIN: '#94a3b8',
  LOGOUT: '#94a3b8',
};

const AuditLog = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState('');

  const load = useCallback(async (targetPage = page, activityType = typeFilter) => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: targetPage };
      if (activityType) params.activity_type = activityType;
      const res = await activityAPI.list(params);
      const data = res.data;
      setActivities(data.results || []);
      setTotal(data.count || 0);
      setTotalPages(Math.max(1, Math.ceil((data.count || 0) / 50)));
      setPage(targetPage);
    } catch (e) {
      setError(
        e?.response?.data?.error ||
        e?.response?.data?.detail ||
        'Failed to load the audit log.'
      );
    } finally {
      setLoading(false);
    }
  }, [page, typeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load(1, typeFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter]);

  const userLabel = (activity) => {
    const u = activity.user_detail;
    if (!u) return 'System';
    return `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username || u.email || 'Unknown';
  };

  const studentLabel = (activity) => {
    const s = activity.student_detail;
    if (!s) return null;
    return s.full_name || `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.email;
  };

  return (
    <div className="students-page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiActivity size={22} color="#011ff3" />
            Audit Log
          </h1>
          <p style={{ margin: '6px 0 0', color: '#94a3b8', fontSize: 14 }}>
            System-wide activity trail — <strong style={{ color: '#64748b' }}>{total}</strong> events.
            Visible to Chairman, Admin, CEO, and COO only.
          </p>
        </div>
        <button className="btn-icon" onClick={() => load(page, typeFilter)} title="Refresh">
          <FiRefreshCw size={15} className={loading ? 'cv-spin' : ''} />
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <FiFilter size={14} color="#94a3b8" />
        <select className="filter-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">All activity types</option>
          {Object.entries(ACTIVITY_TYPE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="empty-state">Loading…</div>
      ) : error ? (
        <div className="error-message" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <FiAlertTriangle size={18} />
          <span>{error}</span>
          <button onClick={() => load(page, typeFilter)} style={{ marginLeft: 'auto', color: '#c33', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }}>
            Retry
          </button>
        </div>
      ) : activities.length === 0 ? (
        <div className="empty-state">No activity recorded yet.</div>
      ) : (
        <>
          <div className="students-table-container">
            <table className="students-table">
              <thead>
                <tr>
                  {['When', 'Type', 'User', 'Description', 'Student'].map(h => <th key={h}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {activities.map((a) => {
                  const color = ACTIVITY_TYPE_COLORS[a.activity_type] || '#94a3b8';
                  const student = studentLabel(a);
                  return (
                    <tr key={a.id}>
                      <td style={{ fontSize: 12, whiteSpace: 'nowrap', color: '#94a3b8' }}>
                        {a.created_at ? new Date(a.created_at).toLocaleString() : '—'}
                      </td>
                      <td>
                        <span className="status-pill" style={{ color }}>
                          {ACTIVITY_TYPE_LABELS[a.activity_type] || a.activity_type}
                        </span>
                      </td>
                      <td><strong>{userLabel(a)}</strong></td>
                      <td style={{ color: '#64748b' }}>{a.description}</td>
                      <td style={{ color: '#94a3b8' }}>{student || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12 }}>
              <button className="btn-icon" disabled={page <= 1} onClick={() => load(page - 1, typeFilter)}>
                ← Prev
              </button>
              <span style={{ color: '#64748b', fontSize: 13 }}>
                Page <strong>{page}</strong> of {totalPages}
                <span style={{ color: '#94a3b8', marginLeft: 8 }}>({total} events)</span>
              </span>
              <button className="btn-icon" disabled={page >= totalPages} onClick={() => load(page + 1, typeFilter)}>
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AuditLog;
